import { useState } from "react";
import type { PermissionCode } from "@koclukpanel/authz";
import { BaglamSeridi, BosDurum, Buton, SekmePaneli } from "@koclukpanel/ui";
import { PERSONEL_SEKMELERI } from "./sekmeler";

/**
 * PERSONEL 360° KABUĞU
 *
 * ================================================================
 * ÖĞRENCİ 360° İLE AYNI KALIP — VE BU KASITLI
 * ================================================================
 * İki ekran aynı işi yapıyor: bir kişiyi seç, hakkındaki her şeyi
 * sekmelerde topla. Farklı kurgulasaydık, aynı soruyu iki farklı
 * yoldan soran bir panel olurdu ve kullanıcı her ekranda yeniden
 * öğrenirdi.
 *
 * Kabuk hangi sekmelerin var olduğunu BİLMİYOR: `sekmeler.tsx`
 * listesini alıyor, izne göre süzüyor, çiziyor.
 *
 * ================================================================
 * SÜZGEÇ BİR KORUMA DEĞİL
 * ================================================================
 * Sekmeyi gizlemek veriyi korumaz; adresi bilen istek atar ve sunucu
 * onu reddeder. Süzmenin amacı kullanıcıya açamayacağı bir sekme
 * göstermemek.
 */
export function Personel360({
  personelId,
  personelAdi,
  izinler,
  onPersonelDegistir,
}: {
  personelId: string;
  personelAdi: string;
  /** `/auth/ben` izin haritası — ham hâliyle geliyor, tipi burada daralıyor */
  izinler: Record<string, string>;
  onPersonelDegistir: () => void;
}) {
  const izinVar = (kod: PermissionCode): boolean => kod in izinler;

  const gorunen = PERSONEL_SEKMELERI.filter((s) => s.izin.some(izinVar));

  const [secilen, setSecilen] = useState<string>(() => gorunen[0]?.anahtar ?? "");

  /*
    SEÇİLİ SEKME KAYBOLABİLİR. Panelde "kullanıcı görünümü" var:
    yönetici başka birinin gözünden bakabiliyor ve izin haritası OTURUM
    ORTASINDA değişiyor. Açık sekme o haritada yoksa kabuk hiçbir hata
    vermeden boş bir panel çizerdi. `useState` başlangıç değeri yalnızca
    ilk çizimde okunuyor; doğrulama çizim anında yapılıyor.
  */
  const aktif = gorunen.find((s) => s.anahtar === secilen) ?? gorunen[0];

  if (!aktif) {
    return (
      <BosDurum
        baslik="Görüntülenecek bölüm yok"
        aciklama="Bu personel için yetkiniz dahilinde açılabilecek bir bölüm bulunamadı."
      />
    );
  }

  const { Icerik } = aktif;

  return (
    <div className="flex flex-col gap-4">
      <BaglamSeridi
        basHarfler={basHarfler(personelAdi)}
        ad={personelAdi}
        eylem={
          <Buton gorunum="ikincil" onClick={onPersonelDegistir}>
            Personel değiştir
          </Buton>
        }
      />

      <SekmePaneli
        sekmeler={gorunen.map((s) => ({ deger: s.anahtar, etiket: s.etiket }))}
        secili={aktif.anahtar}
        onSec={setSecilen}
        etiket="Personel bölümleri"
        kimlikOneki="personel360"
      >
        {/*
          `key`: sekme değiştiğinde içerik SIFIRDAN kuruluyor. Olmasaydı
          React aynı konumdaki iki farklı ekranı eşleştirir ve önceki
          sekmenin iç durumu yeni sekmeye sızardı.
        */}
        <Icerik
          key={aktif.anahtar}
          personelId={personelId}
          personelAdi={personelAdi}
          izinVar={izinVar}
        />
      </SekmePaneli>
    </div>
  );
}

/** "Deniz Yılmaz" -> "DY". Tek kelimeyse ilk iki harf. */
function basHarfler(ad: string): string {
  const parcalar = ad.trim().split(/\s+/).filter(Boolean);
  if (parcalar.length === 0) return "?";
  if (parcalar.length === 1) return parcalar[0]!.slice(0, 2).toLocaleUpperCase("tr-TR");
  return (parcalar[0]![0]! + parcalar[parcalar.length - 1]![0]!).toLocaleUpperCase("tr-TR");
}
